import { useEffect, useState } from 'react';

export const POPUP_W = 380;
export const POPUP_H = 540;

export type PopupVariant =
  | 'create'
  | 'alternates-open'
  | 'already-exists'
  | 'success'
  | 'top-links'
  | 'first-run'
  | 'log-in'
  | 'create-account'
  | 'offline';

interface C2ExtensionPopupProps {
  variant?: PopupVariant;
  initialName?: string;
}

export default function C2ExtensionPopup({ variant = 'create', initialName = '' }: C2ExtensionPopupProps) {
  const [tab, setTab] = useState<'create' | 'top'>(variant === 'top-links' ? 'top' : 'create');

  useEffect(() => {
    setTab(variant === 'top-links' ? 'top' : 'create');
  }, [variant]);

  const authVariant = variant === 'log-in' || variant === 'create-account';
  const showTabs = !authVariant && variant !== 'offline';
  const signedIn = !authVariant;
  const showFooter = !authVariant;

  return (
    <div
      style={{
        width: POPUP_W,
        minHeight: POPUP_H,
        background: 'var(--color-surface)',
        borderRadius: 14,
        overflow: 'hidden',
        fontFamily: 'inherit',
        color: 'var(--color-ink)',
        border: '1px solid var(--color-hairline)',
        boxShadow: '0 20px 60px -20px rgba(20, 18, 35, 0.25)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PopupHeader signedIn={signedIn} authVariant={authVariant} />
      {showTabs && <PopupTabs tab={tab} setTab={setTab} />}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {variant === 'log-in' && <PopupAuth mode="log-in" />}
        {variant === 'create-account' && <PopupAuth mode="create-account" />}
        {variant === 'offline' && <PopupOffline />}
        {variant === 'success' && <PopupSuccess />}
        {variant === 'already-exists' && tab === 'create' && <PopupCreate alreadyExists initialName={initialName} />}
        {variant === 'already-exists' && tab === 'top' && <PopupTopLinks />}
        {variant === 'first-run' && tab === 'create' && <PopupCreate initialName={initialName} />}
        {variant === 'first-run' && tab === 'top' && <PopupTopLinksEmpty />}
        {variant === 'create' && tab === 'create' && <PopupCreate initialName={initialName} />}
        {variant === 'create' && tab === 'top' && <PopupTopLinks />}
        {variant === 'alternates-open' && tab === 'create' && <PopupCreate alternatesOpen initialName={initialName} />}
        {variant === 'alternates-open' && tab === 'top' && <PopupTopLinks />}
        {variant === 'top-links' && tab === 'top' && <PopupTopLinks />}
        {variant === 'top-links' && tab === 'create' && <PopupCreate initialName={initialName} />}
      </div>
      {showFooter && <PopupFooter offline={variant === 'offline'} />}
    </div>
  );
}

// ============================================================
// HEADER
// ============================================================

function PopupHeader({ signedIn, authVariant }: { signedIn: boolean; authVariant: boolean }) {
  return (
    <div
      className="bg-surface flex items-center justify-between"
      style={{
        padding: '14px 16px 12px',
        borderBottom: '1px solid var(--color-hairline)',
        gap: 12,
      }}
    >
      <div className="flex items-center min-w-0" style={{ gap: 9 }}>
        <div
          className="rounded-[10px] flex items-center justify-center font-mono font-bold flex-shrink-0"
          style={{
            width: 30,
            height: 30,
            background: 'var(--accent-soft)',
            color: 'var(--accent-deep)',
            fontSize: 12,
          }}
        >
          go/
        </div>
        <div
          className="font-display font-bold whitespace-nowrap"
          style={{ fontSize: 16, letterSpacing: '-0.01em', color: 'var(--color-ink)' }}
        >
          Tidy Go Links
        </div>
      </div>
      {authVariant ? null : signedIn ? (
        <div className="flex items-center min-w-0 flex-shrink" style={{ gap: 10 }}>
          <span
            className="font-medium overflow-hidden text-ellipsis whitespace-nowrap"
            style={{ fontSize: 12, color: 'var(--accent-deep)', maxWidth: 150 }}
          >
            chris@acme.com
          </span>
          <button
            className="bg-transparent border-0 p-0 cursor-pointer font-semibold flex-shrink-0"
            style={{ fontFamily: 'inherit', fontSize: 12, color: 'var(--color-peach-deep)' }}
          >
            Logout
          </button>
        </div>
      ) : (
        <button
          className="border-0 cursor-pointer font-semibold"
          style={{
            background: 'var(--accent-solid)',
            color: '#fff',
            padding: '6px 14px',
            borderRadius: 999,
            fontFamily: 'inherit',
            fontSize: 12,
          }}
        >
          Log in
        </button>
      )}
    </div>
  );
}

// ============================================================
// TABS
// ============================================================

function PopupTabs({ tab, setTab }: { tab: 'create' | 'top'; setTab: (t: 'create' | 'top') => void }) {
  const tabs: Array<{ key: 'create' | 'top'; label: string }> = [
    { key: 'create', label: 'Create' },
    { key: 'top', label: 'Top Links' },
  ];
  return (
    <div
      className="grid bg-surface"
      style={{
        gridTemplateColumns: '1fr 1fr',
        borderBottom: '1px solid var(--color-hairline)',
      }}
    >
      {tabs.map(t => {
        const active = tab === t.key;
        return (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className="bg-transparent border-0 cursor-pointer transition-colors duration-150"
            style={{
              padding: '12px 0',
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: active ? 700 : 500,
              color: active ? 'var(--accent-deep)' : 'var(--color-ink-muted)',
              borderBottom: `2px solid ${active ? 'var(--accent-deep)' : 'transparent'}`,
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================
// CREATE TAB
// ============================================================

function PopupCreate({
  alternatesOpen: initialOpen = false,
  alreadyExists = false,
  initialName = '',
}: {
  alternatesOpen?: boolean;
  alreadyExists?: boolean;
  initialName?: string;
}) {
  const [name, setName] = useState(initialName);
  const [alts, setAlts] = useState<string[]>(['', '', '']);
  const [open, setOpen] = useState(initialOpen);
  const [url, setUrl] = useState('https://app.rippling.com/time-products/dashboard/time-off/summary');
  const [desc, setDesc] = useState('Submit a vacation request');
  const [focused, setFocused] = useState<string | null>(null);

  return (
    <div
      className="flex-1 flex flex-col overflow-auto"
      style={{ padding: '16px 16px 10px', gap: 14 }}
    >
      {alreadyExists && (
        <div
          style={{
            background: 'var(--accent-tint)',
            border: '1px solid var(--accent-soft)',
            borderRadius: 10,
            padding: '12px 14px',
            fontSize: 13,
            lineHeight: 1.5,
            color: 'var(--color-ink)',
          }}
        >
          <div className="font-medium mb-1">You already have a go/ link for this page</div>
          <div
            className="font-mono font-bold mb-1.5"
            style={{ fontSize: 13, color: 'var(--accent-deep)' }}
          >
            go/q4-plan
          </div>
          <div className="flex font-semibold" style={{ gap: 14, fontSize: 13 }}>
            <button
              className="bg-transparent border-0 p-0 cursor-pointer underline whitespace-nowrap"
              style={{ color: 'var(--accent-deep)', fontFamily: 'inherit', textUnderlineOffset: 3 }}
            >
              Edit
            </button>
            <button
              className="bg-transparent border-0 p-0 cursor-pointer underline whitespace-nowrap"
              style={{ color: 'var(--accent-deep)', fontFamily: 'inherit', textUnderlineOffset: 3 }}
            >
              Test link
            </button>
          </div>
        </div>
      )}

      <FieldGroup label="Go/ Link">
        <GoLinkField
          name={name}
          setName={setName}
          placeholder="pick a memorable name"
          focused={focused === 'name'}
          onFocus={() => setFocused('name')}
          onBlur={() => setFocused(null)}
        />
      </FieldGroup>

      <div>
        <button
          onClick={() => setOpen(o => !o)}
          className="bg-transparent border-0 p-0 cursor-pointer flex items-center font-medium"
          style={{ color: 'var(--accent-deep)', fontFamily: 'inherit', fontSize: 13, gap: 6 }}
        >
          <span style={{ fontSize: 15, lineHeight: 1, fontWeight: 600 }}>{open ? '–' : '+'}</span>
          {open ? 'Hide alternate go/ links' : 'Add alternate go/ links'}
          <span
            title="Alternates are helpful go/ links that point to the same URL (e.g. go/vacation, go/pto, go/takevacation could all point to where you log vacation time)"
            className="rounded-full inline-flex items-center justify-center font-bold"
            style={{
              width: 15,
              height: 15,
              background: 'var(--color-bg-alt)',
              color: 'var(--color-ink-muted)',
              fontSize: 10,
            }}
          >
            ?
          </span>
        </button>
        {open && (
          <div className="flex flex-col mt-2.5" style={{ gap: 8 }}>
            {alts.map((alt, i) => (
              <GoLinkField
                key={i}
                name={alt}
                setName={v => setAlts(a => a.map((x, idx) => (idx === i ? v : x)))}
                placeholder="pick a memorable alternate"
                dim
                focused={focused === `alt-${i}`}
                onFocus={() => setFocused(`alt-${i}`)}
                onBlur={() => setFocused(null)}
              />
            ))}
          </div>
        )}
      </div>

      <FieldGroup label="URL">
        <TextField
          value={url}
          onChange={setUrl}
          focused={focused === 'url'}
          onFocus={() => setFocused('url')}
          onBlur={() => setFocused(null)}
        />
      </FieldGroup>

      <FieldGroup label="Description">
        <TextField
          value={desc}
          onChange={setDesc}
          focused={focused === 'desc'}
          onFocus={() => setFocused('desc')}
          onBlur={() => setFocused(null)}
        />
      </FieldGroup>

      <button
        className="border-0 cursor-pointer font-semibold"
        style={{
          marginTop: 4,
          background: 'var(--accent-solid)',
          color: '#fff',
          borderRadius: 10,
          padding: '13px 16px',
          fontFamily: 'inherit',
          fontSize: 14,
        }}
      >
        Create Link
      </button>
    </div>
  );
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        className="font-bold uppercase mb-1.5"
        style={{ fontSize: 11, color: 'var(--color-ink-muted)', letterSpacing: '0.08em' }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function GoLinkField({
  name,
  setName,
  placeholder,
  dim,
  focused,
  onFocus,
  onBlur,
}: {
  name: string;
  setName: (v: string) => void;
  placeholder: string;
  dim?: boolean;
  focused: boolean;
  onFocus: () => void;
  onBlur: () => void;
}) {
  return (
    <div
      className="flex items-stretch overflow-hidden transition-[border-color,box-shadow] duration-150"
      style={{
        border: `1.5px solid ${focused ? 'var(--accent-deep)' : 'var(--color-hairline-strong)'}`,
        borderRadius: 8,
        background: dim ? 'var(--color-bg-alt)' : 'var(--color-surface)',
        boxShadow: focused ? '0 0 0 3px var(--accent-soft)' : 'none',
      }}
    >
      <span
        className="font-mono font-bold flex items-center"
        style={{
          padding: '10px 12px',
          background: 'var(--color-bg-alt)',
          fontSize: 13,
          color: 'var(--color-ink)',
          borderRight: '1px solid var(--color-hairline)',
        }}
      >
        go/
      </span>
      <input
        value={name}
        onChange={e => setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        className="flex-1 border-0 outline-none font-mono font-medium bg-transparent min-w-0"
        style={{ padding: '10px 12px', fontSize: 13, color: 'var(--color-ink)' }}
      />
    </div>
  );
}

function TextField({
  value,
  onChange,
  focused,
  onFocus,
  onBlur,
}: {
  value: string;
  onChange: (v: string) => void;
  focused: boolean;
  onFocus: () => void;
  onBlur: () => void;
}) {
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      className="w-full font-mono outline-none transition-[border-color,box-shadow] duration-150"
      style={{
        border: `1.5px solid ${focused ? 'var(--accent-deep)' : 'var(--color-hairline-strong)'}`,
        borderRadius: 8,
        background: 'var(--color-surface)',
        padding: '10px 12px',
        fontSize: 12,
        fontWeight: 500,
        color: 'var(--color-ink)',
        boxSizing: 'border-box',
        boxShadow: focused ? '0 0 0 3px var(--accent-soft)' : 'none',
      }}
    />
  );
}

// ============================================================
// TOP LINKS
// ============================================================

const TOP_LINKS = [
  { name: 'oncall', dest: 'pagerduty.com/schedules/acme', clicks: 412, trend: 'up' as const },
  { name: 'roadmap', dest: 'notion.so/acme/q4-roadmap', clicks: 247, trend: 'up' as const },
  { name: 'standup', dest: 'notion.so/acme/eng-standup', clicks: 203, trend: 'flat' as const },
  { name: 'design', dest: 'figma.com/file/acme-ds', clicks: 189, trend: 'up' as const },
  { name: 'okrs', dest: 'docs.google.com/d/okr-q4', clicks: 156, trend: 'down' as const },
  { name: 'payroll', dest: 'gusto.com/acme', clicks: 84 },
  { name: 'brand', dest: 'brand.acme.com/guidelines', clicks: 62 },
  { name: 'incident', dest: 'acme.atlassian.net/wiki/incident', clicks: 48 },
];

function PopupTopLinks() {
  return (
    <div className="flex-1 overflow-auto" style={{ padding: '12px 12px 14px' }}>
      <div
        className="font-bold uppercase"
        style={{
          fontSize: 11,
          color: 'var(--color-ink-muted)',
          letterSpacing: '0.08em',
          padding: '0 4px 8px',
        }}
      >
        Top links · last 30 days
      </div>
      <div className="flex flex-col" style={{ gap: 2 }}>
        {TOP_LINKS.map((l, i) => (
          <TopLinkRow key={l.name} link={l} rank={i + 1} />
        ))}
      </div>
    </div>
  );
}

function TopLinkRow({
  link,
  rank,
}: {
  link: { name: string; dest: string; clicks: number; trend?: 'up' | 'down' | 'flat' };
  rank: number;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="grid items-center cursor-pointer transition-colors duration-150"
      style={{
        gridTemplateColumns: '22px 1fr auto',
        gap: 10,
        padding: '9px 10px',
        borderRadius: 8,
        background: hover ? 'var(--accent-tint)' : 'transparent',
      }}
    >
      <span
        className="font-mono font-semibold text-center"
        style={{
          fontSize: 11,
          color: rank <= 3 ? 'var(--accent-deep)' : 'var(--color-ink-faint)',
        }}
      >
        {rank}
      </span>
      <div className="min-w-0">
        <div
          className="font-mono font-semibold whitespace-nowrap overflow-hidden text-ellipsis"
          style={{ fontSize: 13, color: hover ? 'var(--accent-deep)' : 'var(--color-ink)' }}
        >
          <span className="text-ink-muted">go/</span>
          {link.name}
        </div>
        <div
          className="font-mono whitespace-nowrap overflow-hidden text-ellipsis mt-0.5"
          style={{ fontSize: 11, color: 'var(--color-ink-muted)' }}
        >
          {link.dest}
        </div>
      </div>
      <div className="flex items-center" style={{ gap: 4 }}>
        {link.trend === 'up' && (
          <span style={{ fontSize: 10, color: 'var(--color-mint-deep)' }}>▲</span>
        )}
        {link.trend === 'down' && (
          <span style={{ fontSize: 10, color: 'var(--color-peach-deep)' }}>▼</span>
        )}
        <span
          className="font-mono font-semibold text-right"
          style={{
            fontSize: 11,
            color: 'var(--color-ink-soft)',
            minWidth: 32,
          }}
        >
          {link.clicks}
        </span>
      </div>
    </div>
  );
}

function PopupTopLinksEmpty() {
  return (
    <div
      className="flex-1 flex flex-col items-center text-center"
      style={{ padding: '40px 24px' }}
    >
      <div
        className="rounded-full flex items-center justify-center"
        style={{
          width: 72,
          height: 72,
          background: 'var(--color-lemon)',
          fontSize: 32,
          marginBottom: 16,
        }}
      >
        📊
      </div>
      <h3
        className="font-display font-bold m-0"
        style={{ fontSize: 18, letterSpacing: '-0.01em', color: 'var(--color-ink)' }}
      >
        Nothing to rank{' '}
        <span className="italic" style={{ color: 'var(--accent-deep)' }}>yet</span>
      </h3>
      <p className="text-ink-soft m-0 mt-2 leading-[1.55]" style={{ fontSize: 12, marginInline: 16 }}>
        Create a few go/ links and your team's most-clicked will show up here.
      </p>
    </div>
  );
}

// ============================================================
// SUCCESS / OFFLINE / AUTH (compact)
// ============================================================

function PopupSuccess() {
  return (
    <div className="flex-1 flex flex-col" style={{ padding: '18px 16px' }}>
      <div
        className="flex-1 flex flex-col items-center justify-center text-center"
        style={{
          background: 'var(--color-mint)',
          border: '1px dashed var(--color-mint-deep)',
          borderRadius: 14,
          padding: '20px 20px 22px',
        }}
      >
        <BravocadoMark size={170} />
        <h3
          className="font-display font-extrabold m-0"
          style={{
            fontSize: 26,
            marginTop: 4,
            letterSpacing: '-0.02em',
            color: 'var(--color-ink)',
          }}
        >
          Bravocado!
        </h3>
        <p
          className="leading-[1.4] m-0"
          style={{ fontSize: 14, color: 'var(--color-ink)', marginTop: 6, marginBottom: 18 }}
        >
          You created{' '}
          <span
            className="font-mono font-bold"
            style={{ color: 'var(--color-mint-deep)' }}
          >
            go/q4-plan
          </span>
        </p>
        <div className="flex flex-wrap justify-center" style={{ gap: 8 }}>
          <SuccessBtn primary>Create another</SuccessBtn>
          <SuccessBtn>Test link</SuccessBtn>
          <SuccessBtn>Edit link</SuccessBtn>
        </div>
      </div>
    </div>
  );
}

function SuccessBtn({ children, primary }: { children: React.ReactNode; primary?: boolean }) {
  return (
    <button
      className="cursor-pointer whitespace-nowrap font-semibold"
      style={{
        background: primary ? 'var(--accent-solid)' : 'var(--color-surface)',
        color: primary ? '#fff' : 'var(--color-ink)',
        border: primary ? 'none' : '1px solid var(--color-hairline-strong)',
        borderRadius: 8,
        padding: '8px 14px',
        fontFamily: 'inherit',
        fontSize: 12,
      }}
    >
      {children}
    </button>
  );
}

function PopupOffline() {
  return (
    <div
      className="flex-1 flex flex-col items-center text-center"
      style={{ padding: '36px 24px 24px' }}
    >
      <div
        className="rounded-full flex items-center justify-center mb-4"
        style={{ width: 72, height: 72, background: 'var(--color-lemon)', fontSize: 32 }}
      >
        📡
      </div>
      <h3
        className="font-display font-bold m-0"
        style={{ fontSize: 20, letterSpacing: '-0.01em', color: 'var(--color-ink)' }}
      >
        Can't reach the{' '}
        <span className="italic" style={{ color: 'var(--accent-deep)' }}>server</span>
      </h3>
      <p
        className="text-ink-soft leading-[1.55]"
        style={{ fontSize: 13, margin: '8px 12px 18px' }}
      >
        Your <strong style={{ color: 'var(--color-ink)' }}>most-used links are cached locally</strong> — go/roadmap, go/design and 43 others still work.
      </p>
      <button
        className="cursor-pointer font-semibold"
        style={{
          background: 'var(--color-surface)',
          color: 'var(--color-ink)',
          border: '1px solid var(--color-hairline-strong)',
          borderRadius: 999,
          padding: '9px 16px',
          fontFamily: 'inherit',
          fontSize: 13,
        }}
      >
        Retry connection
      </button>
    </div>
  );
}

function PopupAuth({ mode }: { mode: 'log-in' | 'create-account' }) {
  const isCreate = mode === 'create-account';
  return (
    <div className="flex-1 flex flex-col" style={{ padding: '20px 20px 22px', gap: 12 }}>
      <div className="text-center" style={{ marginBottom: 4 }}>
        <h3
          className="font-display font-bold m-0"
          style={{ fontSize: 18, letterSpacing: '-0.01em', color: 'var(--color-ink)' }}
        >
          {isCreate ? 'Create account' : 'Log in'}
        </h3>
        {isCreate && (
          <p className="text-ink-soft leading-[1.45]" style={{ fontSize: 12, margin: '6px 16px 0' }}>
            Use your work email so your team can share the same go/ links.
          </p>
        )}
      </div>
      <AuthBtn provider="google" />
      <AuthBtn provider="microsoft" />
      <AuthBtn provider="zoho" />
      <AuthBtn provider="email" />
      <div
        className="text-center text-ink-soft"
        style={{ marginTop: 'auto', paddingTop: 8, fontSize: 12 }}
      >
        {isCreate ? (
          <>Have an account? <AuthSwitchLink>Sign in</AuthSwitchLink></>
        ) : (
          <>New here? <AuthSwitchLink>Create account</AuthSwitchLink></>
        )}
      </div>
    </div>
  );
}

function AuthSwitchLink({ children }: { children: React.ReactNode }) {
  return (
    <button
      className="bg-transparent border-0 p-0 cursor-pointer font-semibold"
      style={{ fontFamily: 'inherit', fontSize: 12, color: 'var(--accent-deep)' }}
    >
      {children}
    </button>
  );
}

function AuthBtn({ provider }: { provider: 'google' | 'microsoft' | 'zoho' | 'email' }) {
  const meta = {
    google: { label: 'Continue with Google', icon: <GoogleIcon /> },
    microsoft: { label: 'Continue with Microsoft', icon: <MicrosoftIcon /> },
    zoho: { label: 'Continue with Zoho', icon: <ZohoIcon /> },
    email: { label: 'Continue with Email', icon: <EmailIcon /> },
  }[provider];
  const [hover, setHover] = useState(false);
  return (
    <button
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="cursor-pointer flex items-center justify-center w-full font-semibold"
      style={{
        gap: 10,
        padding: '12px 16px',
        background: 'var(--color-surface)',
        border: `1px solid ${hover ? 'var(--color-hairline-strong)' : 'var(--color-hairline)'}`,
        borderRadius: 10,
        fontFamily: 'inherit',
        fontSize: 14,
        color: 'var(--color-ink)',
        transition: 'border-color 120ms ease, background 120ms ease',
      }}
    >
      <span
        className="flex items-center justify-center flex-shrink-0"
        style={{ width: 18, height: 18 }}
      >
        {meta.icon}
      </span>
      <span>{meta.label}</span>
    </button>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.8.54-1.84.87-3.05.87-2.34 0-4.33-1.58-5.04-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.96 10.73A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.18.28-1.73V4.94H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.06l3-2.33Z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.94l3 2.33C4.67 5.16 6.66 3.58 9 3.58Z" />
    </svg>
  );
}
function MicrosoftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16">
      <rect x="0" y="0" width="7" height="7" fill="#F25022" />
      <rect x="9" y="0" width="7" height="7" fill="#7FBA00" />
      <rect x="0" y="9" width="7" height="7" fill="#00A4EF" />
      <rect x="9" y="9" width="7" height="7" fill="#FFB900" />
    </svg>
  );
}
function ZohoIcon() {
  return (
    <div
      className="rounded font-extrabold flex items-center justify-center"
      style={{
        width: 18,
        height: 18,
        background: '#2F7A44',
        color: '#fff',
        fontSize: 11,
        letterSpacing: '-0.02em',
      }}
    >
      Z
    </div>
  );
}
function EmailIcon() {
  return (
    <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
      <rect x="1" y="1" width="16" height="12" rx="2" stroke="#1E1B2E" strokeWidth="1.4" />
      <path d="M2 3 L9 8 L16 3" stroke="#1E1B2E" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ============================================================
// FOOTER
// ============================================================

function PopupFooter({ offline }: { offline: boolean }) {
  return (
    <div
      className="bg-surface flex items-center justify-between"
      style={{
        padding: '10px 16px',
        borderTop: '1px solid var(--color-hairline)',
        fontSize: 12,
        color: 'var(--color-ink-muted)',
      }}
    >
      <div className="flex items-center" style={{ gap: 7 }}>
        <span
          className="rounded-full"
          style={{
            width: 7,
            height: 7,
            background: offline ? 'var(--color-peach-deep)' : 'var(--color-mint-deep)',
            boxShadow: offline ? 'none' : '0 0 0 3px var(--color-mint)',
          }}
        />
        <span className="font-medium">{offline ? 'Offline' : 'Connected'}</span>
      </div>
      <button
        className="bg-transparent border-0 p-0 cursor-pointer font-semibold"
        style={{ fontFamily: 'inherit', fontSize: 12, color: 'var(--accent-deep)' }}
      >
        My Account
      </button>
    </div>
  );
}

// ============================================================
// BRAVOCADO — happy avocado SVG (used in success state)
// ============================================================

function BravocadoMark({ size = 170 }: { size?: number }) {
  return (
    <svg width={size} height={size * 0.95} viewBox="0 0 200 190" style={{ display: 'block' }}>
      <defs>
        <radialGradient id="bravo-body" cx="0.4" cy="0.35" r="0.8">
          <stop offset="0%" stopColor="#B8DC8F" />
          <stop offset="55%" stopColor="#7FBB5F" />
          <stop offset="100%" stopColor="#5A9442" />
        </radialGradient>
        <radialGradient id="bravo-face" cx="0.4" cy="0.35" r="0.7">
          <stop offset="0%" stopColor="#E4F0C8" />
          <stop offset="100%" stopColor="#B6D98D" />
        </radialGradient>
        <radialGradient id="bravo-pit" cx="0.35" cy="0.3" r="0.8">
          <stop offset="0%" stopColor="#B07845" />
          <stop offset="100%" stopColor="#6B4220" />
        </radialGradient>
      </defs>
      <g fill="#F5B82E">
        <path d="M36 28 L40 40 L52 44 L40 48 L36 60 L32 48 L20 44 L32 40 Z" opacity="0.95" />
        <path d="M170 32 L173 41 L182 44 L173 47 L170 56 L167 47 L158 44 L167 41 Z" opacity="0.9" />
        <path d="M160 14 L162 20 L168 22 L162 24 L160 30 L158 24 L152 22 L158 20 Z" opacity="0.75" />
        <circle cx="24" cy="72" r="3" opacity="0.7" />
        <circle cx="180" cy="78" r="2.5" opacity="0.6" />
      </g>
      <g>
        <path d="M74 102 Q50 82 42 52" stroke="#3E6E2C" strokeWidth="7" strokeLinecap="round" fill="none" />
        <circle cx="42" cy="50" r="9" fill="#7FBB5F" stroke="#3E6E2C" strokeWidth="2.5" />
        <path d="M38 49 L46 49 M38 53 L46 53" stroke="#3E6E2C" strokeWidth="1.3" strokeLinecap="round" opacity="0.6" />
      </g>
      <g>
        <path d="M126 102 Q150 82 158 52" stroke="#3E6E2C" strokeWidth="7" strokeLinecap="round" fill="none" />
        <circle cx="158" cy="50" r="9" fill="#7FBB5F" stroke="#3E6E2C" strokeWidth="2.5" />
        <path d="M154 49 L162 49 M154 53 L162 53" stroke="#3E6E2C" strokeWidth="1.3" strokeLinecap="round" opacity="0.6" />
      </g>
      <g>
        <path d="M82 176 Q78 168 80 158" stroke="#3E6E2C" strokeWidth="7" strokeLinecap="round" fill="none" />
        <ellipse cx="78" cy="180" rx="13" ry="6" fill="#1E1B2E" />
        <ellipse cx="78" cy="178" rx="13" ry="3" fill="#3A3550" />
      </g>
      <g>
        <path d="M118 172 Q124 162 122 154" stroke="#3E6E2C" strokeWidth="7" strokeLinecap="round" fill="none" />
        <ellipse cx="124" cy="176" rx="13" ry="6" fill="#1E1B2E" />
        <ellipse cx="124" cy="174" rx="13" ry="3" fill="#3A3550" />
      </g>
      <path
        d="M100 36 C 72 36, 58 66, 58 100 C 58 138, 76 162, 100 162 C 124 162, 142 138, 142 100 C 142 66, 128 36, 100 36 Z"
        fill="url(#bravo-body)"
        stroke="#3E6E2C"
        strokeWidth="3"
      />
      <ellipse cx="100" cy="90" rx="32" ry="34" fill="url(#bravo-face)" />
      <circle cx="100" cy="118" r="20" fill="url(#bravo-pit)" stroke="#4A2C12" strokeWidth="1.5" />
      <ellipse cx="92" cy="110" rx="6" ry="3" fill="#D89A68" opacity="0.5" transform="rotate(-25 92 110)" />
      <path d="M80 86 Q86 78 92 86" stroke="#1E1B2E" strokeWidth="3.2" fill="none" strokeLinecap="round" />
      <path d="M108 86 Q114 78 120 86" stroke="#1E1B2E" strokeWidth="3.2" fill="none" strokeLinecap="round" />
      <path d="M79 84 L76 81" stroke="#1E1B2E" strokeWidth="2" strokeLinecap="round" />
      <path d="M121 84 L124 81" stroke="#1E1B2E" strokeWidth="2" strokeLinecap="round" />
      <path d="M90 98 Q100 94 110 98 Q112 108 100 116 Q88 108 90 98 Z" fill="#1E1B2E" />
      <path d="M95 104 Q100 112 105 104 Q103 110 100 110 Q97 110 95 104 Z" fill="#E24D5E" />
      <path d="M94 99 Q100 97 106 99" stroke="#fff" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <ellipse cx="76" cy="102" rx="5" ry="3" fill="#F5A28E" opacity="0.7" />
      <ellipse cx="124" cy="102" rx="5" ry="3" fill="#F5A28E" opacity="0.7" />
      <ellipse cx="80" cy="60" rx="10" ry="14" fill="#fff" opacity="0.25" transform="rotate(-20 80 60)" />
    </svg>
  );
}
